<!--Copyright (c) PikPok. All rights reserved-->
<template>
  <v-container :fluid="true" class="ma-0 pa-0" v-if="loading">
    <v-row class="text-white bg-yellow-darken-3 pa-3 mt-0">
      <router-link to="/">
        <v-icon color="white" icon="mdi-less-than" size="x-large" class="pr-5 pl-5"></v-icon>
      </router-link>
      <v-icon color="white" icon="mdi-currency-usd" size="x-large" class="pr-5 pl-5"></v-icon>
      <h3 class="pt-1">
        {{ bugReport.strings.BUG_BILLING_AND_SUBSCRIPTION }}
      </h3>
    </v-row>
    <v-row v-if="!formSubmit">
      <v-col offset-lg="2" lg="8" offset="1" cols="10">
        <v-select
            v-model="formDetails.purchase_and_refund_support"
            :items="bugReport.purchase_and_refund_support"
            :rules="[v => !!v]"
            :label=bugReport.strings.BUG_BILLING_AND_SUBSCRIPTION
            :placeholder=bugReport.strings.BUG_CHOOSE_OPTION
            required variant="solo-filled" return-object
        ></v-select>
        <v-form ref="form" lazy-validation v-if="!!formDetails.purchase_and_refund_support">
          <v-select
              v-model="formDetails.game"
              :items="appList"
              :rules="[v => !!v]"
              :label=bugReport.strings.BUG_GAME
              :placeholder=bugReport.strings.BUG_CHOOSE_OPTION
              required variant="solo-filled"
          ></v-select>
          <v-select
              v-model="formDetails.platform"
              :items="bugReport.platforms"
              :rules="[v => !!v]"
              :label=bugReport.strings.BUG_PLATFORM
              :placeholder=bugReport.strings.BUG_CHOOSE_OPTION
              required variant="solo-filled"
          ></v-select>
          <!-- iOS refund requests -->
          <div
              v-if="formDetails.platform === 'iOS'
              && (formDetails.purchase_and_refund_support.value === 'Refund requests' || formDetails.purchase_and_refund_support.value === 'Double charges or overcharges')"
              class="ma-3">
            <p>
              Due to Apple's strict privacy features, we do not have access to the tools required to process refunds for purchases made through their platform.
              As such, we recommend that you reach out directly to Apple to resolve your request.
            </p>
            <p class="mt-5">
              Here’s how you can proceed:
            </p>
            <ol class="ml-7 mt-2">
              <li>
                <b>Report a Problem to Apple:</b>
                <ul class="ml-10 mt-2">
                  <li>Visit the
                    <a href="https://reportaproblem.apple.com/" target="_blank" style="text-decoration: underline;">
                      Apple - Report a Problem
                    </a>
                    page.
                  </li>
                  <li>Log in using the Apple ID associated with the purchase.</li>
                  <li>Select the purchase in question, provide a detailed description of the issue, and submit your request.
                  </li>
                </ul>
              </li>
              <li class="mt-2">
                <b>Tips for a Successful Request:</b>
                <ul class="ml-10 mt-2">
                  <li>Reporting the issue as soon as possible increases the likelihood of a favorable resolution.</li>
                  <li>Clearly explain the nature of the problem to help Apple evaluate your case quickly.</li>
                </ul>
              </li>
            </ol>
            <p class="mt-5">
              We regret that we’re unable to assist further due to Apple’s privacy policies but are confident that their
              support team will be able to assist you.
            </p>
            <p class="mt-5">If there’s anything else we can help you with, please let us know.</p>
          </div>
          <!-- Steam PC/MAC refund requests -->
          <div
              v-else-if="formDetails.platform === 'PC'
              && formDetails.purchase_and_refund_support.value === 'Refund requests'"
              class="ma-3">
            <p>
              Steam manages all refund requests directly through their platform. To request a refund, please follow these steps:
            </p>
            <ol class="ml-7 mt-2">
              <li>
                <b>Submit a Refund Request via Steam:</b>
                <ul class="ml-10 mt-2">
                  <li>Visit the
                    <a href="https://help.steampowered.com/en/faqs/view/5FDE-BA65-ACCE-A411#:~:text=What%20can%20I%20request%20a,offline%20and%20shared%20library%20playtime"
                       style="text-decoration: underline;" target="_blank">Steam Support page.</a>
                  </li>
                  <li>Log into your Steam account.</li>
                  <li>Select the game or purchase in question and click "I’d like a refund."</li>
                </ul>
              </li>
              <li class="mt-2">
                <b>Eligibility Criteria:</b>
                <ul class="ml-10 mt-2">
                  <li>Refund requests must typically be made within <b>14 days of purchase.</b></li>
                  <li>The game must have been played for <b>less than 2 hours</b> (this includes offline and shared library playtime).
                  </li>
                  <li>
                    Additional conditions may apply, as outlined in Steam’s
                    <a href="https://help.steampowered.com/en/faqs/view/5FDE-BA65-ACCE-A411#:~:text=What%20can%20I%20request%20a,offline%20and%20shared%20library%20playtime"
                       style="text-decoration: underline;" target="_blank">refund policy.</a>
                  </li>
                </ul>
              </li>
            </ol>
            <p class="mt-5">
              Unfortunately, we are unable to process refunds directly, as Steam handles all transactions and refunds through their system.
              If you encounter any issues with the process, we recommend contacting Steam Support for further assistance.
            </p>
          </div>
          <!-- other form options -->
          <div v-else-if="formDetails.platform">
            <v-text-field
                v-model="formDetails.email"
                :rules="[v => !!v, v => !v || /\s*\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})\s*/.test(v)]"
                :label=bugReport.strings.BUG_EMAIL
                :placeholder=bugReport.strings.BUG_EMAIL_HINT
                required variant="solo-filled"
            ></v-text-field>
            <v-text-field
                v-model="formDetails.order_id"
                :label=bugReport.strings.BUG_ANDROID_USER_ORDER_ID
                :placeholder=bugReport.strings.BUG_ANDROID_USER_ORDER_ID_INFO
                required variant="solo-filled"
            ></v-text-field>
            <!-- How to find Order ID -->
            <v-expansion-panels class="mb-5">
              <v-expansion-panel>
                <v-expansion-panel-title class="text-subtitle-1 bg-amber-lighten-4">
                  {{ bugReport.strings.PRIVACY_HOW_FIND }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <h3>If You Have an Android Device:</h3>
                  <ul class="ml-7 mt-5">
                    <li>
                      Provide either:
                      <ul class="ml-5 mt-2">
                        <li>The <b>email address</b> associated with your <b>Google Play account</b>, or</li>
                        <li>A <b>screenshot</b> of your order confirmation receipt.</li>
                      </ul>
                    </li>
                  </ul>
                  <p class="ma-3">The order confirmation receipt must display the following details:</p>
                  <ul class="ml-10">
                    <li><b>Item Name</b></li>
                    <li><b>Order Date</b></li>
                    <li><b>Order Number</b> (This starts with "GPA" followed by a long string of numbers, e.g., GPA.1234-1234-1234-12345).
                    </li>
                  </ul>

                  <h3 class="mt-5">If You Made the Purchase on an Apple Device:</h3>
                  <ul class="mt-3 ml-7">
                    <li>
                      Follow the directions in this Apple support article to locate your receipt details:
                      <a href="https://support.apple.com/en-is/HT204088" target="_blank"
                         style="text-decoration: underline">Apple Support: View Purchase History.</a>
                    </li>
                  </ul>

                  <h3 class="mt-5">Important Notes:</h3>
                  <ul class="mt-3 ml-7">
                    <li><b>Receipts Required:</b> We can only track purchases using the official <b>confirmation
                      receipts.</b></li>
                    <li><b>Unsupported Proofs:</b> Screenshots from bank accounts or payment methods cannot be used, as
                      they do not display the <b>item name</b> or <b>order number.</b></li>
                  </ul>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            <!-- Google Captcha -->
            <GoogleRecaptcha ref="recaptcha" @verify="onSubmit"></GoogleRecaptcha>
            <!-- Submit -->
            <v-btn
                color="yellow-darken-3"
                class="mt-4 mb-5 text-white"
                block
                @click="executeRecaptcha"
            >
              {{ bugReport.strings.BUG_SUBMIT }}
            </v-btn>
          </div>
        </v-form>
      </v-col>
    </v-row>
    <!-- Success or Error response after submit -->
    <v-row v-else class="mt-10">
      <v-col offset-lg="2" lg="8" offset="1" cols="10">
        <p v-if="error">{{ bugReport.strings.PRIVACY_ERROR }}</p>
        <p v-else v-html="bugReport.strings.BUG_THANKS_MESSAGE"></p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {mapActions, mapState} from "vuex";
import GoogleRecaptcha from "@/components/Recaptcha";

export default {
  name: "BillingAndSubscription",
  components: {GoogleRecaptcha},
  data() {
    return {
      formDetails: {},
      loading: false,
      shareExtraInfo: true,
      noShareWarning: false,
      formSubmit: false,
      error: false,
      copyFormDetails: {},
      appList: [],
    }
  },
  computed: {
    ...
        mapState({
          bugReport: state => state.supportStore.bugReport,
        }),
  },
  mounted() {
    this.getBugReport({
      params: {
        ...this.$route.query
      }
    }).then((response) => {
      this.loading = true
      this.formDetails = JSON.parse(JSON.stringify(response.data.form_details))
      response.data.appList.forEach(app => {
        this.appList.push({
          value: app[0],
          title: app[1],
        })
        // Contact us form - Set game name
        app[0].toLowerCase() == this.formDetails.game.toLowerCase()
            ? this.formDetails.game = app[0]
            : this.formDetails.game === '' && app[0].toLowerCase() == this.formDetails.appname.toLowerCase()
                ? this.formDetails.game = app[0] : null
      })

      // form - Set platform name
      this.formDetails.platform !== ""
          ? this.formDetails.platform = response.data.platforms.filter(p => p.value.toLowerCase() === this.formDetails.platform.toLowerCase())[0].value
          : this.formDetails.platform === "" && this.formDetails.os !== ""
              ? this.formDetails.platform = response.data.platforms.filter(p => p.value.toLowerCase() === this.formDetails.os.toLowerCase())[0].value
              : null
    })
  },
  methods: {
    async onSubmit(result) {
      const {valid} = await this.$refs.form.validate()
      if (valid) {
        this.postBugReport({
          data: {
            ...this.formDetails,
            grecaptchaResponse: result,
            formType: 'purchase_and_refund_support'
          }
        }).then((response) => {
          this.formSubmit = true
          this.error = response.data.error
        })
      } else {
        // Scroll to first error field
        this.$nextTick(() => {
          let topOfElement = document.querySelector('.v-field--error.v-field--error:first-of-type').offsetTop - 100
          window.scroll({top: topOfElement, behavior: "smooth"})
        })
      }
    },

    executeRecaptcha() {
      this.$refs.recaptcha.execute()
    },

    ...mapActions([
      'getBugReport',
      'postBugReport'
    ])
  }
}
</script>

<style scoped lang="scss">
@media only screen and (max-width: 550px) {
  .small-button-height {
    height: 70px;
  }
}
</style>
